import React from "react";

import { BetaBadge, CapabilityCard, FeatureCard, NAVY, StrategyColumn } from "./WelcomeParts";
import { WELCOME_FEATURES, type WelcomeFeature } from "./welcomeFeatures";

/* ─── Slide 1: Welcome Overview ─── */

export function SlideWelcome() {
  return (
    <div className="flex flex-col items-center px-8 py-10">
      <h2
        className="font-cvcDisplay font-medium text-[34px] leading-[40px] text-center max-w-[560px] mb-4"
        style={{ color: NAVY }}
      >
        Welcome to DealOS, CVC&apos;s proprietary AI platform.
      </h2>

      <p className="font-cvcBody text-base leading-6 text-center max-w-[560px] mb-8 text-cvc-text">
        Right now the platform can help you search and interrogate CVC&apos;s past PE and
        Private Credit deals, their IC papers and PE DD docs, and find the colleagues with the
        most relevant deal experience, with more powerful features on the way.
      </p>

      <p className="text-xs font-cvcBody uppercase tracking-wider text-cvc-text opacity-40 mb-6">
        DealOS Features
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-[680px]">
        {WELCOME_FEATURES.map((feature) => (
          <FeatureCard
            key={feature.id}
            icon={feature.icon}
            title={feature.title}
            description={feature.summary}
            isBeta
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Feature slides: one per WELCOME_FEATURES entry ─── */

/**
 * Narrow dialogs wrap the tiles two per row, wide tiles taking a full row.
 * From md up they share one row: each tile gets its own track, never
 * narrower than its longest word (e.g. "Infrastructure"), and wide tiles
 * take twice the leftover space.
 */
function FeatureStrategies({ feature }: { feature: WelcomeFeature }) {
  const tracks = feature.strategies
    .map((s) => `minmax(min-content, ${s.isWide ? 2 : 1}fr)`)
    .join(" ");
  return (
    <>
      <p className="font-cvcBody text-base leading-[18px] text-cvc-text mb-3">
        {feature.strategyHeading}
      </p>
      <div
        className="grid grid-cols-2 gap-3 md:[grid-template-columns:var(--strategy-tracks)]"
        style={{ "--strategy-tracks": tracks } as React.CSSProperties}
      >
        {feature.strategies.map((strategy) => (
          <div key={strategy.name} className={strategy.isWide ? "col-span-2 md:col-span-1" : ""}>
            <StrategyColumn {...strategy} />
          </div>
        ))}
      </div>
    </>
  );
}

export function FeatureSlide({ feature }: { feature: WelcomeFeature }) {
  const FeatureIcon = feature.icon;
  const [first, second, ...bottomRow] = feature.capabilities;

  return (
    <div className="px-8 py-8">
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <FeatureIcon size={36} color={NAVY} />
        <h2 className="font-cvcDisplay font-medium text-[34px] leading-[40px]" style={{ color: NAVY }}>
          {feature.title}
        </h2>
        <BetaBadge />
      </div>

      {/* Top row: description + 2 capability cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-[rgba(52,52,52,0.03)] rounded-[10px] p-5 flex items-start">
          <p className="font-cvcBody text-base leading-6 text-cvc-text">{feature.slideIntro}</p>
        </div>
        <CapabilityCard {...first} />
        <CapabilityCard {...second} />
      </div>

      {/* Bottom row: 3 capability cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {bottomRow.map((capability) => (
          <CapabilityCard key={capability.title} {...capability} />
        ))}
      </div>

      <FeatureStrategies feature={feature} />
    </div>
  );
}
